import React, { useState, useEffect } from "react";
import { Folder, Search, RefreshCw, EyeOff, HardDrive, ChevronUp, ChevronDown } from "lucide-react";
import baseapi from "../../../services/baseapi"; // Adjust import path
import { showError } from "../../../utils/toast"; // Adjust import path
import { Pagination } from "./Pagination";

interface SharedFolder {
    id: number;
    name: string;
    path: string;
    description: string;
    hidden: boolean;
    active_connections: number;
    permissions: string | string[]; // JSON string array or array
    folder_type: string | null;
}

interface AssetSharedFoldersProps {
    assetId: string | number;
    client_id: string | number;
}

export default function AssetSharedFolders({ assetId, client_id }: AssetSharedFoldersProps) {
    const [folders, setFolders] = useState<SharedFolder[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchInput, setSearchInput] = useState("");

    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    // Refresh Key (used to force useEffect to re-run when clicking refresh)
    const [refreshKey, setRefreshKey] = useState(0);

    const fetchFolders = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * limit;

            const queryObj: any = {
                asset_id: assetId,
                client_id: client_id
            };

            if (searchTerm) {
                queryObj.$or = [
                    { name: { $ilike: `%${searchTerm}%` } },
                    { path: { $ilike: `%${searchTerm}%` } }
                ];
            }

            const params: any = {
                skip,
                limit,
                query: JSON.stringify(queryObj)
            };

            // Add API Sorting if requested
            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/asset_shared_folders", { params });

            if (response.data.status) {
                setFolders(response.data.data || []);
                setTotalRecords(response.data.total || 0);
            } else {
                showError(response.data.message || "Failed to load shared folders");
                setFolders([]);
                setTotalRecords(0);
            }
        } catch (error: any) {
            showError(error.message || "Failed to load shared folders");
            setFolders([]);
            setTotalRecords(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (assetId) {
            fetchFolders();
        }
        // Added refreshKey to the dependency array
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, limit, searchTerm, assetId, sortConfig, refreshKey]);

    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        setSearchTerm(searchInput.trim());
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setCurrentPage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey }: { label: string; sortKey: string }) => {
        const isActive = sortConfig?.key === sortKey;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer group select-none hover:bg-slate-100 dark:hover:bg-gray-700 transition-colors"
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-orange-600 dark:text-orange-400" />
                            ) : (
                                <ChevronDown size={12} className="text-orange-600 dark:text-orange-400" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-slate-400 dark:text-gray-200 group-hover:text-slate-600 dark:group-hover:text-white" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const renderPermissions = (permData: string | string[]) => {
        if (!permData) return <span className="text-slate-400 dark:text-gray-200">-</span>;

        let parsed: string[] = [];
        if (Array.isArray(permData)) {
            parsed = permData;
        } else if (typeof permData === "string") {
            try {
                const p = JSON.parse(permData);
                if (Array.isArray(p)) {
                    parsed = p;
                } else {
                    return <span className="text-slate-600 dark:text-gray-200 text-[10px] truncate max-w-[150px] block" title={permData}>{permData}</span>;
                }
            } catch (e) {
                return <span className="text-slate-600 dark:text-gray-200 text-[10px] truncate max-w-[150px] block" title={permData}>{permData}</span>;
            }
        } else {
            return <span className="text-slate-400 dark:text-gray-200">-</span>;
        }

        if (parsed.length === 0) return <span className="text-slate-400 dark:text-gray-200">-</span>;

        return (
            <div className="flex flex-wrap gap-1">
                {parsed.map((perm: string, idx: number) => {
                    if (typeof perm !== 'string') return null;
                    const [user, level] = perm.split(':');
                    const isFull = level?.toLowerCase().includes('full');
                    const isRead = level?.toLowerCase().includes('read');

                    return (
                        <span
                            key={idx}
                            className={`text-[9px] px-1.5 py-0.5 rounded border leading-tight ${isFull ? 'bg-rose-50 dark:bg-red-500/10 text-rose-700 dark:text-red-300 border-rose-200 dark:border-red-500/20' :
                                isRead ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-500/20' :
                                    'bg-slate-100 dark:bg-gray-700/50 text-slate-700 dark:text-gray-300 border-slate-200 dark:border-gray-600/50'
                                }`}
                            title={perm}
                        >
                            {user ? user.replace(/\\\\/g, '\\') : perm} {level ? `(${level})` : ''}
                        </span>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="flex flex-col h-full text-slate-900 dark:text-gray-200 relative p-2 pb-6">
            <div className="py-2 flex items-center justify-between gap-4 shrink-0 mb-3 border-b border-slate-200 dark:border-gray-700 pb-3">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5 shadow-sm">
                        <Folder className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                        <h2 className="text-sm font-bold text-slate-900 dark:text-white leading-tight">Shared Folders</h2>
                        <p className="text-slate-500 dark:text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {totalRecords || '...'} shares detected
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={() => {
                            // Reset state directly (fetchFolders() is no longer called here)
                            setCurrentPage(1);
                            setSearchInput("");
                            setSearchTerm("");
                            // Trigger re-render explicitly if values didn't actually change
                            setRefreshKey(prev => prev + 1);
                        }}
                        disabled={loading}
                        className="p-2 bg-white dark:bg-gray-800 hover:bg-slate-100 dark:hover:bg-gray-700 text-slate-600 dark:text-gray-300 hover:text-slate-900 dark:hover:text-white border border-slate-200 dark:border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-orange-500' : ''}`} />
                    </button>
                    <form onSubmit={handleSearchSubmit} className="relative">
                        <Search className="w-3.5 h-3.5 text-slate-400 dark:text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Search name or path..."
                            className="w-64 bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-gray-500 focus:outline-none focus:border-orange-500 transition-colors h-8 shadow-sm"
                        />
                    </form>
                </div>
            </div>

            {/* Table Area */}
            <div className="bg-white dark:bg-gray-900/40 rounded-md border border-slate-200 dark:border-gray-700 overflow-hidden flex flex-col relative shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full table-fixed text-left min-w-[850px]">
                        <colgroup>
                            <col className="w-[18%]" />
                            <col className="w-[22%]" />
                            <col className="w-[12%]" />
                            <col className="w-[15%]" />
                            <col className="w-[8%]" />
                            <col className="w-[25%]" />
                        </colgroup>
                        <thead className="bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700">
                            <tr>
                                <SortableHeader label="Share Name" sortKey="name" />
                                <SortableHeader label="Local Path" sortKey="path" />
                                <SortableHeader label="Folder Type" sortKey="folder_type" />
                                <SortableHeader label="Description" sortKey="description" />
                                <SortableHeader label="Active Connections" sortKey="active_connections" />
                                <th className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-300 uppercase tracking-wider">Permissions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-800/50">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="h-32">
                                        <div className="flex flex-col items-center justify-center space-y-3 h-full">
                                            <div className="w-6 h-6 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : folders.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="h-32">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-60 h-full">
                                            <Folder size={32} className="text-slate-400 dark:text-gray-200 mb-2" />
                                            <p className="text-[11px] font-bold text-slate-600 dark:text-gray-200 uppercase tracking-widest">No shared folders found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                folders.map((folder) => (
                                    <tr key={folder.id} className="hover:bg-slate-50 dark:hover:bg-gray-800/40 transition-colors duration-100">
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-2">
                                                {folder.hidden ? (
                                                    <span title="Hidden Share (ends with $)" className="shrink-0 flex items-center">
                                                        <EyeOff size={12} className="text-slate-400 dark:text-gray-200" />
                                                    </span>
                                                ) : (
                                                    <Folder size={12} className="text-amber-500 shrink-0" />
                                                )}
                                                <span className="text-[11px] font-bold text-slate-900 dark:text-white truncate" title={folder.name}>
                                                    {folder.name}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1.5 text-slate-600 dark:text-gray-300">
                                                <HardDrive size={10} className="text-slate-400 dark:text-gray-200 shrink-0" />
                                                <span className="text-[10px] font-mono truncate" title={folder.path}>{folder.path || "-"}</span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] text-slate-700 dark:text-gray-300 font-medium px-1.5 py-0.5 rounded bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700/60 inline-block truncate max-w-full" title={folder.folder_type || "Unknown"}>
                                                {folder.folder_type || "Unknown"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="text-[10px] text-slate-600 dark:text-gray-200 truncate block" title={folder.description}>
                                                {folder.description || "-"}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            <div className="flex items-center gap-1.5">
                                                <span className={`text-[10px] font-bold ${folder.active_connections > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-slate-400 dark:text-gray-200'}`}>
                                                    {folder.active_connections ? folder.active_connections : '-'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-3 py-2">
                                            {renderPermissions(folder.permissions)}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination
                    currentPage={currentPage}
                    totalPages={Math.max(1, Math.ceil(totalRecords / limit))}
                    onPageChange={setCurrentPage}
                    itemsPerPage={limit}
                    totalItems={totalRecords}
                    onItemsPerPageChange={(val: number) => {
                        setLimit(val);
                        setCurrentPage(1);
                    }}
                />
            </div>
        </div>
    );
}